import { groupBy } from '@progress/kendo-data-query'

import { get, post, postJSON, put } from './api_helper'

import { downloadFile } from './downloadFile'
import { esObjetoVacio, agruparPor, sinEstado } from './utils'

// eslint-disable-next-line camelcase
import { dateTimeToDateForms, dateToYY_MM_DD, toDate } from './utilsDate'

import { EstadoReservaDetalle } from '../interfaces/reservasEstados'

import { apiSucesorValido } from '@/common/constants/api'

import { Estado } from '@/common/interfaces/commons'
import { Reserva } from '@/common/interfaces/reserva'
import { ReservaDetalle } from '@/common/interfaces/reservasDetalle'
import { ActividadReserva, ActividadReservaDetalle, SucesorValido } from '@/common/interfaces/respuestas'

import { ReservaDetallesChangeSend, ReservaDetalleSend } from '@/common/interfaces/sends'

import { env } from '@/core/config/env'
import { reportarError } from '@/core/telemetria/reportarError'

/**
 * Actualiza el detalle de la reserva con el comnetario que ingresa el usuario
 * @param reservaD
 * @param comentario
 * @returns
 */
export const putReservaDetalle = (reservaD: ReservaDetalle, comentario: string): Promise<any> => {
  const sendData: ReservaDetalleSend = {
    Acomodacion: reservaD.Acomodacion,
    AcomodacionDescripcion: reservaD.AcomodacionDescripcion,
    ActividadId: reservaD.ActividadId,
    Cantidad: reservaD.Cantidad,
    CantidadAdulto: reservaD.CantidadAdulto,
    CantidadInfante: reservaD.CantidadInfante,
    CantidadNino: reservaD.CantidadNino,
    Comentario: comentario,
    EstadoId: reservaD.EstadoId,
    Fecha: reservaD.Fecha,
    FechaFin: dateTimeToDateForms(reservaD.FechaFin),
    FechaInicio: dateTimeToDateForms(reservaD.FechaInicio),
    ReservaDetalleId: reservaD.ReservaDetalleId,
    ReservaId: reservaD.ReservaId,
    TotalPasajeros: reservaD.TotalPasajeros,
    VueloId: reservaD.VueloId,
    EsReservaVigente: reservaD.EsReservaVigente,
  }

  return put(`ReservaDetalles(${reservaD.ReservaDetalleId})`, sendData)
}

/**
 * Obtiene el detalle de una reserva y muestra dependiendo de los estados
 * @param reservaId
 * @param estadoReserva
 * @param estadosReservas
 * @returns
 */
export const getReservaDetalleBy = async (
  reservaId: number,
  estadoReserva: number,
  estadosReservas: any,
  estadoPath: string,
): Promise<any> => {
  if (sinEstado(estadoReserva)) {
    throw new Error('No se encontró el estado de la reserva. Por favor, contacte a soporte.')
  }

  return get(
    `ReservaDetalles?$expand=Estado($select=Nombre),Actividad($expand=Comentario($expand=TipoComentario)) & $filter=ReservaId eq ${reservaId}`,
  ).then((resp) => {
    const reservas = resp.value
    let newReservas: ReservaDetalle[] = []

    if (estadosReservas[estadoPath].EstadoId === estadosReservas['Negado'].EstadoId) {
      newReservas = reservas.filter((r: ReservaDetalle) => r.EstadoId === estadosReservas['Negado'].EstadoId)

      return addComentario(newReservas)
    }

    if (estadosReservas[estadoPath].EstadoId === estadosReservas['Cancelado'].EstadoId) {
      newReservas = reservas.filter((r: ReservaDetalle) => r.EstadoId === estadosReservas['Cancelado'].EstadoId)

      return addComentario(newReservas)
    }

    if (estadoReserva === estadosReservas['Reservado'].EstadoId) {
      newReservas = reservas.filter((r: ReservaDetalle) => r.EsReservaVigente === true)

      return addComentario(newReservas)
    }

    newReservas = reservasProcesar(reservas, estadoReserva, estadosReservas)

    if (
      estadoReserva === estadosReservas['Modificación Pendiente'].EstadoId ||
      estadoReserva === estadosReservas['Cancelación Pendiente'].EstadoId
    ) {
      newReservas = quitarReservaByEstado(newReservas, 'Cancelado', estadosReservas)
      newReservas = quitarReservaByEstado(newReservas, 'Negado', estadosReservas)
    }

    const newReservasComentarios = newReservas.map((rd: ReservaDetalle) => {
      rd.FechaInicio = toDate(rd.FechaInicio)
      rd.FechaFin = toDate(rd.FechaFin)
      const comentariosSerivicios = rd.Actividad.Comentario.map((cs: any) => {
        if (cs.TipoComentario.Nombre === 'Servicio') {
          return cs.Comentario1
        }

        return []
      })
      const comentariosOperactivos = rd.Actividad.Comentario.map((co: any) => {
        if (co.TipoComentario.Nombre === 'Operación') {
          return co.Comentario1
        }

        return []
      })

      rd.comentarioServicio = comentariosSerivicios.join('\n')
      rd.comentarioOperativo = comentariosOperactivos.join('\n')

      return rd
    })

    return ordenarReservas(newReservasComentarios, estadosReservas)
  })
}

const addComentario = (reservas: ReservaDetalle[]): ReservaDetalle[] => {
  return reservas.map((rd: ReservaDetalle) => {
    rd.FechaInicio = toDate(rd.FechaInicio)
    rd.FechaFin = toDate(rd.FechaFin)
    const comentariosSerivicios = rd.Actividad.Comentario.map((cs: any) => {
      if (cs.TipoComentario.Nombre === 'Servicio') {
        return cs.Comentario1
      }

      return []
    })
    const comentariosOperactivos = rd.Actividad.Comentario.map((co: any) => {
      if (co.TipoComentario.Nombre === 'Operación') {
        return co.Comentario1
      }

      return []
    })

    rd.comentarioServicio = comentariosSerivicios.join('\n')
    rd.comentarioOperativo = comentariosOperactivos.join('\n')

    return rd
  })
}

const quitarReservaByEstado = (reservas: ReservaDetalle[], estado: string, estados: any): ReservaDetalle[] => {
  const reservasCanceladas = reservas.filter((r: ReservaDetalle) => r.EstadoId === estados[estado].EstadoId)
  const idCanceladas = reservasCanceladas.map((r: ReservaDetalle) => r.ReservaDetalleId)
  let reservasNoCanceladas: ReservaDetalle[] = []

  if (idCanceladas.length > 0) {
    reservasNoCanceladas = reservas.filter((r: ReservaDetalle) => {
      return idCanceladas.indexOf(r.ReservaDetalleId) === -1
    })
  } else {
    reservasNoCanceladas = reservas
  }

  return reservasNoCanceladas
}

const ordenarReservas = (reservas: ReservaDetalle[], estados: any): ReservaDetalle[] => {
  const soloReservados = reservas.filter((r: ReservaDetalle) => r.EstadoId === estados['Reservado'].EstadoId)
  const otrosEstados = reservas.filter((r: ReservaDetalle) => r.EstadoId !== estados['Reservado'].EstadoId)

  otrosEstados.sort((a, b) => (a.FechaInicio > b.FechaInicio ? 1 : -1))
  soloReservados.sort((a, b) => (a.FechaInicio > b.FechaInicio ? 1 : -1))

  return [...otrosEstados, ...soloReservados]
}

const reservasProcesar = (reservas: ReservaDetalle[], estadoReserva: number, estados: any): ReservaDetalle[] => {
  const reservaProcesada: ReservaDetalle[] = []
  const reservasPorActividad = agruparPor(reservas, (r) => r.ActividadId)

  Object.values(reservasPorActividad).forEach((reserva) => {
    if (reserva.length > 1 && estadoReserva === estados['Modificación Pendiente'].EstadoId) {
      const reservaModificadaResult = obtenerReservaModificada(reserva, estadoReserva, estados)

      reservaProcesada.push(reservaModificadaResult)
    } else {
      const reservaVigente = reserva.find((r: ReservaDetalle) => r.EsReservaVigente === true)

      reservaProcesada.push(reservaVigente || reserva[0])
    }
  })

  return reservaProcesada
}

const obtenerReservaModificada = (
  reservas: ReservaDetalle[],
  estadoReserva: number,
  estados: { [key: string]: Estado },
): ReservaDetalle => {
  const estadoModificacion = estados['Modificación Pendiente'].EstadoId
  const estadoSolicitada = estados['Modificación Solicitada'].EstadoId
  const estadoReservado = estados['Reservado'].EstadoId

  const reservaModificada = reservas.find(
    (r: ReservaDetalle) =>
      r.EsReservaVigente === false && (r.EstadoId === estadoModificacion || r.EstadoId === estadoSolicitada),
  )

  if (!reservaModificada) {
    throw new Error('No se encontró la reserva modificada. Por favor, contacte a soporte.')
  }

  const reservaVigente = reservas.find(
    (r: ReservaDetalle) => r.EsReservaVigente === true && r.EstadoId === estadoReservado,
  )

  if (!reservaVigente) {
    throw new Error('No se encontró la reserva vigente. Por favor, contacte a soporte.')
  }

  return {
    ...reservaModificada,
    CantidadAdulto: reservaModificada.CantidadAdulto - reservaVigente.CantidadAdulto,
    CantidadNino: reservaModificada.CantidadNino - reservaVigente.CantidadNino,
    CantidadInfante: reservaModificada.CantidadInfante - reservaVigente.CantidadInfante,
    Cantidad: reservaModificada.Cantidad - reservaVigente.Cantidad,
  }
}

/**
 * Obtiene las actividaddes de una reserva por fechas y tipo de actividad
 * @param tipoActividad
 * @param inicio
 * @param fin
 * @returns
 */
export const getActividadReservaBusiness = async (tipoActividad: string, inicio?: Date, fin?: Date): Promise<any> => {
  const feF = fin !== undefined ? dateToYY_MM_DD(fin) : ' '
  const feI = inicio !== undefined ? dateToYY_MM_DD(inicio) : ' '

  return get(`ActividadReservasBusiness(${feI},${feF},${tipoActividad})`)
}

/**
 * Obtiene una actividad de una reserva por
 * @param actividadReservaCabeceraId
 * @param bookingId
 * @param proveedorId
 * @param actividadId
 * @param tipo
 * @returns
 */
export const getActividadReservaBy = async (
  actividadReservaCabeceraId: number,
  bookingId: number,
  proveedorId: number,
  actividadId: number,
  tipo: string,
): Promise<any> => {
  const arb = await get(
    `ActividadReservasDetalleBusiness(${actividadReservaCabeceraId},${bookingId},${proveedorId},${actividadId},${tipo})`,
  )

  return arb.map((ar: ActividadReservaDetalle) => {
    ar.fechaInicio = toDate(ar.fechaInicio)
    ar.fechaFin = toDate(ar.fechaFin)

    return ar
  })
}

/**
 * Obtiene todos los detalles de varias reservas
 * @param promises
 * @returns
 */
export const getDetallesAll = async (promises: any): Promise<any> => {
  return Promise.all(promises).then((resp) => {
    let result: any = []

    result = [].concat(...resp)

    return result
  })
}

export const postGenerarReservas = async (datos: ActividadReserva, ard: ActividadReservaDetalle[]): Promise<any> => {
  const { proveedorId, bookingId } = datos
  const actividades: number[] = []

  ard.forEach((bd) => {
    actividades.push(bd.actividadId)
  })
  const datosEnvio = {
    ProveedorId: proveedorId,
    BookingId: bookingId,
    ActividadId: actividades,
  }

  return post(`GenerarReservasBusiness`, datosEnvio)
}

const getDetalles = async (bookings: any, tipo: string): Promise<any> => {
  return Promise.all(
    bookings.map(async (b: any) => {
      const actividades: string[] = []

      const detalle = await getActividadReservaBy(
        b.actividadReservaCabeceraId,
        b.bookingId,
        b.proveedorId,
        b.actividadId,
        tipo,
      )

      detalle.forEach((d: any) => {
        actividades.push(d.actividadId)
      })

      return Object.assign(b, {
        ActividadId: actividades,
      })
    }),
  )
}

/**
 * Procesa en forma masiva la generación de las reservas
 * @param bookings
 * @param tipo
 * @returns
 */
export const multiProcessGenerarReservas = async (bookings: ActividadReserva[], tipo: string): Promise<any> => {
  const bookingsNoBlock = bookings.filter((b) => b.bloqueoReserva === false)
  const result = await getDetalles(bookingsNoBlock, tipo)
  const promesasGenerarReservas: any[] = []

  result.forEach((pba: any) => {
    const sendData = {
      ProveedorId: pba.proveedorId,
      BookingId: pba.bookingId,
      ActividadId: pba.ActividadId,
    }

    promesasGenerarReservas.push(post('GenerarReservasBusiness', sendData))
  })

  return Promise.all(promesasGenerarReservas)
}

/**
 *  Obtiene todas las reservas dependiendo del tipo de actividad con estado RP o CP
 * @param tipoActividad Tipo Hotel | Alimento
 * @returns Promise<Reserva[]>
 */
export const getReservasTipoEstados = async (
  tipoActividad: string,
  estados: number[],
  filtroReservado: string,
  estadosReserva: any,
): Promise<Reserva[]> => {
  let filtro = ''

  if (estados.length === 0 || estados[0] === 0) {
    return []
  }

  if (estados[0] === estadosReserva['Negado'].EstadoId || estados[0] === estadosReserva['Cancelado'].EstadoId) {
    filtro = `$filter=TipoActividad eq '${tipoActividad}' and (EstadoId in (${estados}) or ReservaDetalle/any(rd:rd/EstadoId eq ${estados[0]}))`
  } else {
    filtro = `$filter=TipoActividad eq '${tipoActividad}' and EstadoId in (${estados}) ${filtroReservado}`
  }

  const resp =
    await get(`Reservas?ReservaId,EstadoId,EstadoFuturoId,ProveedorId,BookingId,TipoReservaId,CartaEnviada,UrlCartaEnviada,TipoActividad,Eliminado,
    &$expand=Estado($Select=Nombre),Proveedor($Select=RazonSocial,NombreCompleto),
    Booking($Select=BookingId,ClienteId,EstadoId,EjecutivoComercialId,Bloqueado,BookingCode,TipoBookingId;
        $expand=Cliente($Select=Nombre),
        EjecutivoComercial($Select=Nombre,Apellido;
            $expand=AsignacionEjecutivoOperativo($Select=AsignacionEjecutivoOperativoId;$expand=AgenteOperativo($Select=Nombre,Apellido,Cargo)))),
        TipoReserva($Select=Nombre) && ${filtro}`)

  return resp.value.map((reserva: Reserva) => {
    let ejecutivoName: string = ''
    let agenteName: string = ''
    let clienteName: string = ''

    if (reserva.Booking.EjecutivoComercial !== undefined) {
      ejecutivoName = `${reserva.Booking.EjecutivoComercial.Nombre} ${reserva.Booking.EjecutivoComercial.Apellido}`
      if (reserva.Booking.EjecutivoComercial.AsignacionEjecutivoOperativo[0] !== undefined) {
        agenteName = `${reserva.Booking.EjecutivoComercial.AsignacionEjecutivoOperativo[0].AgenteOperativo.Nombre} ${reserva.Booking.EjecutivoComercial.AsignacionEjecutivoOperativo[0].AgenteOperativo.Apellido}`
      }
      clienteName = `${reserva.Booking.Cliente.Nombre}`
    }
    const booking = {
      ...reserva.Booking,
      EjecutivoComercialNames: ejecutivoName,
      ClienteNames: clienteName,
      AgenteOperativoNames: agenteName,
    }

    reserva.Booking = booking
    reserva.Booking.FechaInicio = toDate(reserva.Booking.FechaInicio)
    reserva.Booking.FechaFin = toDate(reserva.Booking.FechaFin)

    return reserva
  })
}

/**
 * Filtra el total de reservas por el estado o estados enviados
 * @param data
 * @param estados
 * @returns
 */
export const reservaByEstado = (data: Reserva[], estados: number[]): Reserva[] => {
  if (estados.length > 1) {
    return data.filter((r) => r.EstadoId === estados[0] || r.EstadoId === estados[1])
  }

  return data.filter((r) => estados[0] === r.EstadoId)
}

/**
 *  Cuenta las reservas por estado
 * @param reservas
 * @param estadosActivos
 * @param estadosActuales
 * @returns
 */
export const totalEstadosReserva = (reservas: Reserva[], estadosActivos: any, estadosActuales: number[]): any => {
  const rp = reservaByEstado(reservas, [
    estadosActivos['Reserva Pendiente'].EstadoId,
    estadosActivos['Modificación Pendiente'].EstadoId,
  ]).length
  const cp = reservaByEstado(reservas, [estadosActivos['Cancelación Pendiente'].EstadoId]).length

  if (estadosActuales.length === 0) {
    return { rp, cp }
  }
  if (estadosActuales.includes(estadosActivos['Reserva Pendiente'].EstadoId)) {
    return { rp }
  }
  if (estadosActuales.includes(estadosActivos['Cancelación Pendiente'].EstadoId)) {
    return { cp }
  }
}

/**
 * Obtiene todos los sucesores validos
 * @returns
 */
export const getSucesoresValidos = async (): Promise<SucesorValido[]> => {
  const svResp = await get(apiSucesorValido)

  return svResp.value
}

/**
 * Obtiene los estados dependiendo del origen
 * @param origen
 * @param sv
 * @returns
 */
export const getDestinoByOrigen = (origen: number, sv: SucesorValido[]): EstadoReservaDetalle[] => {
  const destino: EstadoReservaDetalle[] = []

  sv.forEach((sucesor) => {
    if (sucesor.Origen === origen) {
      destino.push({
        id: sucesor.Destino,
        name: sucesor.DestinoNavigation.Nombre,
      })
    }
  })

  return destino
}

/**
 * Envia lista de pasajeros al proveedor
 * @param bookingId
 * @param proveedorId
 * @returns
 */
export const postEnviarListaPasajeros = async (bookingId: number, proveedorId: number): Promise<any> => {
  return post(`EnviarListaPasajeros(${bookingId},${proveedorId})`, {})
}

/**
 * Envia al proceso de eliminar por su id
 * @param reservaId
 * @returns
 */
export const postEliminarReservas = async (reservaId: number): Promise<any> => {
  return post(`EliminarReservas(${reservaId})`, {})
}

/**
 * Proceso post para negar la reserva
 * @param reservaId
 * @returns
 */
export const postNegarReservas = async (reservaId: number): Promise<any> => {
  return post(`NegarReservas(${reservaId})`, {})
}
/**
 * Prepara la reserva para ser enviada a procesar este proceso trabaja para una o varias
 * @param sendDetalle
 * @param tipo
 * @param reservaId
 * @returns
 */
const prepareParamsReserva = (sendDetalle: ReservaDetallesChangeSend[], tipo: string, reservaId?: number): any => {
  let sendData: any[] = []

  if (tipo === 'Hotel') {
    sendData.push({
      ReservaId: reservaId,
      // Se arma un objeto nuevo sin `reservaId`. Antes era `delete sd.reservaId`, que MUTABA los
      // detalles que vienen del estado de redux: quien despues filtrara por ese campo -como hace
      // postProcesarReserva en cada vuelta- ya no encontraba nada.
      ReservasDetalles: sendDetalle.map((sd: any) => {
        const { reservaId: _reservaId, ...resto } = sd

        return resto
      }),
    })
  }
  if (tipo === 'Alimento') {
    sendData = groupBy(sendDetalle, [{ field: 'reservaId' }]).map((sd: any) => ({
      ReservaId: sd.items[0].reservaId,
      ReservasDetalles: sd.items.map((i: any) => ({
        reservaDetalleId: i.reservaDetalleId,
        estadoActualizadoId: i.estadoActualizadoId,
      })),
    }))
  }

  return sendData
}

/**
 * Actualiza los estados de los detalles de la reserva a un estado futuro seleccionado
 * @param enviarDetalle
 * @param tipo
 * @param reservaId
 * @returns
 */
export const postActualizarEstadoFuturoReservas = (
  enviarDetalle: ReservaDetallesChangeSend[],
  tipo: string,
  reservaId: number,
): any => {
  const datosEnvio = prepareParamsReserva(enviarDetalle, tipo, reservaId)

  return postJSON(`ActualizarEstadoFuturoReservas(${tipo})`, datosEnvio)
}

/**
 * Post para generar la carta de reserva
 * @param sendDetalle
 * @param tipoProducto
 * @param enviarCorreo
 * @param reservaId
 * @returns
 */
export const postGenerarCartaReserva = (
  enviarDetalle: ReservaDetallesChangeSend[],
  tipoProducto: string,
  enviarCorreo: boolean,
  reservaId?: number,
): Promise<any> => {
  const datosEnvio = prepareParamsReserva(enviarDetalle, tipoProducto, reservaId)

  return postJSON(`GenerarCartaReserva(${tipoProducto},${enviarCorreo})`, datosEnvio)
}

/**
 * Post para procesar una reserva
 * @param enviarDetalle
 * @param tipo
 * @param reservaId
 * @returns
 */
export const postProcesarReserva = (
  enviarDetalle: ReservaDetallesChangeSend[],
  tipo: string,
  reservaId: any,
): Promise<any> => {
  let enviarDatos: any

  if (typeof reservaId === 'number') {
    enviarDatos = prepareParamsReserva(enviarDetalle, tipo, reservaId)
  }
  if (Array.isArray(reservaId)) {
    enviarDatos = []
    reservaId.forEach((r) => {
      enviarDatos.push(
        ...prepareParamsReserva(
          enviarDetalle.filter((d) => d.reservaId === r),
          tipo,
          r,
        ),
      )
    })
  }

  return postJSON('ProcesarReservas', enviarDatos)
}

export const listoParaConfirmar = (reserva: Reserva, detalle: ReservaDetalle[], estados: any): boolean => {
  if (esObjetoVacio(reserva) || esObjetoVacio(detalle)) {
    return true
  }
  if (reserva.CartaEnviada === true) {
    let respuesta = true

    detalle.forEach((d) => {
      if (estadoParaConfirmar(d.EstadoId, estados)) {
        respuesta = false
      }
    })

    return respuesta
  }

  return true
}

export const listoParaConfirmarTodos = (detalle: ReservaDetallesChangeSend[]): boolean => {
  if (esObjetoVacio(detalle)) {
    return false
  }
  const vectorExisteCartaEnviada: boolean[] = []

  detalle.forEach((d) => {
    if (d.cartaEnviada) {
      vectorExisteCartaEnviada.push(true)
    } else {
      vectorExisteCartaEnviada.push(false)
    }
  })
  if (vectorExisteCartaEnviada.includes(true)) {
    return true
  }

  return false
}

export const listoParaEnviarCarta = (change: ReservaDetallesChangeSend[], estados: any): boolean => {
  if (change.length === 0) {
    return false
  }
  const vectorEstado: boolean[] = []

  change.forEach((c) => {
    if (estadosFinales(c.estadoActualizadoId, estados) || estadosIntermedios(c.estadoActualizadoId, estados)) {
      vectorEstado.push(true)
    } else {
      vectorEstado.push(false)
    }
  })
  if (vectorEstado.includes(false)) {
    return false
  }

  return true
}

export const listoParaEnviarMultiplesCartas = (change: ReservaDetallesChangeSend[], estados: any): boolean => {
  if (change.length === 0) {
    return false
  }
  const vectoEstado: boolean[] = []

  change.forEach((c) => {
    if (estadosIntermedios(c.estadoActualizadoId, estados)) {
      vectoEstado.push(true)
    }
  })
  if (vectoEstado.includes(true)) {
    return true
  }

  return false
}

const estadosIntermedios = (estado: number, estados: any): boolean => {
  switch (estado) {
    case estados['Lista de Espera'].EstadoId: // lista de espera
      return true

    case estados['Reserva Solicitada'].EstadoId: // reserva solicitada
      return true

    case estados['Cancelación Solicitada'].EstadoId: // Cancelación solicitada
      return true

    case estados['Lista de Espera Acordada'].EstadoId: // lista de espera acordada
      return true

    case estados['Modificación Solicitada'].EstadoId: // Modificación solicitada
      return true

    default:
      return false
  }
}

export const estadosFinales = (estado: number, estados: any): boolean => {
  switch (estado) {
    case estados['Cancelado'].EstadoId: // Cancelado
      return true

    case estados['Negado'].EstadoId: // Negado
      return true

    case estados['Reservado'].EstadoId: // Reservado
      return true

    default:
      return false
  }
}

export const estadoParaConfirmar = (estado: number, estados: any): boolean => {
  switch (estado) {
    case estados['Reserva Solicitada'].EstadoId: // reserva solicitada
      return true

    case estados['Cancelación Solicitada'].EstadoId: // Cancelación solicitada
      return true

    case estados['Reservado'].EstadoId: // Reservado
      return true

    case estados['Modificación Solicitada'].EstadoId: // Modificación solicitada
      return true

    default:
      return false
  }
}

export const soloSolicitados = (detalle: ReservaDetallesChangeSend[], estados: any): number[] => {
  const reservas: number[] = []

  detalle.forEach((cambios: any) => {
    if (
      cambios.estadoActualizadoId === estados['Reserva']['Reserva Solicitada'].EstadoId ||
      cambios.estadoActualizadoId === estados['Reserva']['Modificación Solicitada'].EstadoId
    ) {
      reservas.push(cambios.reservaId)
    }
  })

  return reservas
}

export const contarActividadesBloqueadas = (actividadReservas: any): number => {
  let contarNoBloqueadas = 0

  actividadReservas.forEach((ac: any) => {
    if (!ac.bloqueoReserva) {
      contarNoBloqueadas += 1
    }
  })

  return contarNoBloqueadas
}

/**
 * Construye la URL para descargar la carta de reserva usando el nuevo endpoint
 * @param reservaId - ID de la reserva (puede ser ReservaCentralizadaId o ReservaId)
 * @returns URL completa para descargar la carta de reserva
 */
export const construirUrlDescargaCarta = (reservaId: number): string => {
  return `${env.BASE_URL}/ReservaCentralizadaBusiness/DescargarCarta/${reservaId}`
}

/**
 * Descarga la carta de reserva programáticamente llamando al API
 * @param reservaId - ID de la reserva
 * @param fileName - Nombre opcional del archivo (por defecto 'carta-reserva.pdf')
 */
/**
 * Descarga la copia en Excel de la carta, archivada junto al PDF cuando se generó.
 * @param reservaId - ID de la reserva centralizada
 * @param fileName - Nombre opcional del archivo
 */
export const descargarCartaReservaExcel = async (
  reservaId: number,
  fileName: string = 'carta-reserva.xlsx',
): Promise<void> => {
  await descargarCartaReserva(reservaId, fileName, 'DescargarCartaExcel')
}

export const descargarCartaReserva = async (
  reservaId: number,
  fileName: string = 'carta-reserva.pdf',
  endpoint: 'DescargarCarta' | 'DescargarCartaExcel' = 'DescargarCarta',
): Promise<void> => {
  try {
    const url = `${env.BASE_URL}/ReservaCentralizadaBusiness/${endpoint}/${reservaId}`

    // Hacer la llamada al API con autenticación
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Error al descargar: ${response.status} ${response.statusText}`)
    }

    // Convertir la respuesta a blob
    const blob = await response.blob()

    // Crear URL temporal para el blob
    const blobUrl = window.URL.createObjectURL(blob)

    // Crear enlace temporal y activar descarga
    const link = document.createElement('a')

    link.href = blobUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()

    // Limpiar recursos
    document.body.removeChild(link)
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    reportarError(error, 'Error al descargar carta de reserva')
    throw error
  }
}

/**
 * Construye la URL para descargar el reporte de boletos usando el nuevo endpoint
 * @param reservaId - ID de la reserva (puede ser ReservaCentralizadaId o ReservaId)
 * @returns URL completa para descargar el reporte de boletos
 */
export const construirUrlDescargaReporteBoleto = (reservaId: number): string => {
  return `${env.BASE_URL}/DescargarReporte/${reservaId}`
}

/**
 * Descargar el reporte de reserva boletos
 */
export const descargarReporteBoleto = async (reservaId: number): Promise<void> => {
  const url = construirUrlDescargaReporteBoleto(reservaId)

  await downloadFile(url, 'reporte-boletos.xlsx', 'Error al descargar el reporte de boletos:')
}

/**
 * Función auxiliar para obtener el token de acceso
 */
const getAccessToken = async (): Promise<string> => {
  // Importar dinámicamente para evitar dependencias circulares
  const { authHeader } = await import('./authHeader')
  const { accessToken } = await authHeader()

  return accessToken || ''
}
